import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

/**
 * Install-as-app prompt:
 *  - Chrome/Android/desktop: catches the `beforeinstallprompt` event and shows
 *    a "Install VERIFIED" toast button.
 *  - iOS Safari: shows a one-time "Add to Home Screen" tip card (Apple does not
 *    expose an auto-prompt API).
 */
const DISMISS_KEY = "verified.pwa.dismissed";

function isIosSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/i.test(ua);
  const webkit = /WebKit/i.test(ua);
  const notChrome = !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  return iOS && webkit && notChrome;
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function PwaInstallPrompt() {
  const [event, setEvent] = useState(null);
  const [iosTip, setIosTip] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    return !!localStorage.getItem(DISMISS_KEY);
  });

  useEffect(() => {
    if (isStandalone() || dismissed) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS Safari has no event — show the tip after a short delay
    if (isIosSafari()) {
      const t = setTimeout(() => setIosTip(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onPrompt);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [dismissed]);

  const dismiss = () => {
    setEvent(null);
    setIosTip(false);
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  const install = async () => {
    if (!event) return;
    event.prompt();
    try {
      await event.userChoice;
    } catch { /* noop */ }
    dismiss();
  };

  if (dismissed || isStandalone()) return null;
  if (event) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] bento-card px-4 py-3 flex items-center gap-3 max-w-sm w-[calc(100%-2rem)] shadow-[0_10px_40px_rgba(0,0,0,0.6)]"
        data-testid="pwa-install-banner"
      >
        <Download size={18} className="text-electric flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-display font-bold uppercase tracking-wider text-white">Install VERIFIED</div>
          <div className="text-[11px] text-white/50 mt-0.5">Add to your home screen for a one-tap, full-screen experience.</div>
        </div>
        <button onClick={install} className="btn-primary text-[11px]" data-testid="pwa-install-btn">Install</button>
        <button onClick={dismiss} className="text-white/30 hover:text-white" aria-label="Dismiss" data-testid="pwa-dismiss"><X size={14} /></button>
      </div>
    );
  }
  if (iosTip) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] bento-card px-4 py-3 max-w-sm w-[calc(100%-2rem)] shadow-[0_10px_40px_rgba(0,0,0,0.6)]"
        data-testid="pwa-ios-tip"
      >
        <div className="flex items-start gap-3">
          <Share size={18} className="text-electric flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-display font-bold uppercase tracking-wider text-white">Add VERIFIED to your home screen</div>
            <div className="text-[11px] text-white/60 mt-1 leading-relaxed">
              Tap the <strong className="text-electric">Share</strong> icon at the bottom of Safari, then choose <strong className="text-electric">Add to Home Screen</strong>.
            </div>
          </div>
          <button onClick={dismiss} className="text-white/30 hover:text-white" aria-label="Dismiss"><X size={14} /></button>
        </div>
      </div>
    );
  }
  return null;
}
